// Lightweight WebView/native bridge for receiving auth tokens via postMessage
// The native app (iOS/Android) should post messages to the WebView as:
//   { type: 'auth-token', accessToken: string }
//   { type: 'auth-refresh', accessToken: string }
// Optional: { type: 'auth-logout' }

export type NativeAuthMessage =
  | {
      type: 'auth-token';
      accessToken: string;
    }
  | {
      type: 'auth-refresh';
      accessToken: string;
    }
  | {
      type: 'auth-logout';
    };

export type Unsubscribe = () => void;

type TokenListener = (message: NativeAuthMessage) => void;

const listeners: Set<TokenListener> = new Set();

export function isRunningInWebView(): boolean {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('webview') === '1') {
      return true; // testing override
    }
  } catch {
    // ignore URL parse errors
  }
  if ((window as any).__EncoreNativeWebView === true) {
    return true; // native iOS/Android marker
  }
  if (typeof (window as any).ReactNativeWebView !== 'undefined') {
    return true; // RN WebView
  }
  return false;
}

export function subscribeToNativeAuth(listener: TokenListener): Unsubscribe {
  if (listeners.size === 0) {
    window.addEventListener('message', handleWindowMessage);
    // Some native SDKs send messages via document instead of window
    document.addEventListener('message', handleDocumentMessage as any);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('message', handleWindowMessage);
      document.removeEventListener('message', handleDocumentMessage as any);
    }
  };
}

function handleWindowMessage(event: MessageEvent) {
  const data = normalizeMessageData(event.data);
  if (!data) {
    return;
  }
  emit(data);
}

function handleDocumentMessage(event: MessageEvent | CustomEvent) {
  const data = normalizeMessageData((event as any).data);
  if (!data) {
    return;
  }
  emit(data);
}

function normalizeMessageData(raw: any): NativeAuthMessage | null {
  // Some native wrappers wrap JSON string in data
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  if (
    (parsed.type === 'auth-token' || parsed.type === 'auth-refresh') &&
    typeof parsed.accessToken === 'string'
  ) {
    return {
      type: parsed.type,
      accessToken: parsed.accessToken,
    };
  }
  if (parsed.type === 'auth-logout') {
    return { type: 'auth-logout' };
  }
  return null;
}

function emit(message: NativeAuthMessage) {
  listeners.forEach(listener => listener(message));
}

export function requestTokenFromNative() {
  // Provide a way for the webview to ask the native app for the token
  try {
    // 1) React Native WebView bridge
    const rnwv = (window as any).ReactNativeWebView;
    if (rnwv && typeof rnwv.postMessage === 'function') {
      rnwv.postMessage(JSON.stringify({ type: 'request-auth-token' }));
      return;
    }

    // 2) iOS WKWebView message handler bridge
    const webkit = (window as any).webkit;
    if (webkit?.messageHandlers?.EncoreNative?.postMessage) {
      webkit.messageHandlers.EncoreNative.postMessage({
        type: 'request-auth-token',
      });
      return;
    }

    // 3) Android addJavascriptInterface-style bridge
    const androidBridge = (window as any).EncoreNative;
    if (androidBridge?.postMessage) {
      androidBridge.postMessage(JSON.stringify({ type: 'request-auth-token' }));
      return;
    }
  } catch {
    // eslint-disable-next-line no-console
    console.error('Error requesting token from native');
  }
}
